<template>
	<Trap :disabled="!showTrap">
		<div
			v-if="hasContentSlot"
			ref="container"
			:class="{
				'open': isOpen,
				'oversized': isOversized
			}"
			class="overlay scroll"
			data-cy="overlay"
			role="dialog"
			aria-relevant="additions text"
			aria-live="polite"
			aria-atomic="false"
		>
			<div
				@keyup.enter="close"
				@keyup.space="close"
			>
				<helio-icon
					v-if="closeable && uploadCloseable"
					class="overlay__close"
					data-cy="overlay-close"
					token="times"
					weight="light"
					tabindex="2"
					:aria-label="$t('aria-label.close-button')"
					@click="close"
					@keyup.esc="close"
				/>
			</div>

			<div
				v-if="icon"
				class="overlay__icon-container"
			>
				<helio-icon
					class="overlay__icon"
					:token="icon"
					:color="iconColor"
					:weight="iconWeight"
					:rounded="true"
				/>
			</div>

			<div
				v-if="title"
				ref="heading"
				class="overlay__heading"
			>
				<h2
					v-if="title"
					class="overlay__title"
				>
					{{ title }}
				</h2>

				<h3
					v-if="subtitle && !renderSubtitleInHtml"
					class="overlay__subtitle"
				>
					{{ subtitle }}
				</h3>

				<h3
					v-else-if="subtitle && renderSubtitleInHtml"
					class="overlay__subtitle"
					v-html="subtitle"
				/>
			</div>

			<div
				v-if="hasContentSlot"
				ref="content"
				:style="`max-width: ${maxWidth}px`"
				class="overlay__content"
				tabindex="1"
			>
				<slot name="content" />
			</div>

			<div
				v-if="hasFooterSlot"
				:style="`max-width: ${maxWidth}px`"
				class="overlay__footer"
			>
				<slot name="footer" />
			</div>
		</div>
	</Trap>
</template>

<script>
import Trap from 'vue-focus-lock';
import Icon from '../icon/Icon.vue';

export default {
	name: 'Overlay',

	components: {
		Trap: Trap,
		'helio-icon': Icon,
	},

	props: {
		closeable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the Overlay can be closed by a user',
		},

		icon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear in the Button',
		},

		iconColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the colour of the icon. The colour provided must be one of those in the $colors-list.',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the icon',
		},

		maxWidth: {
			type: [Number, String],
			required: false,
			default: 700,
			validator: maxWidth => maxWidth >= 0 || 'none',
			description: 'Sets the maximum width of the Overlay',
		},

		renderSubtitleInHtml: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Decides whether we should render the subtitle with v-html or not',
		},

		subtitle: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Overlay\'s subtitle',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Overlay\'s title',
		},
	},

	slots: {
		content: {
			required: false,
			scoped: false,
			description: 'Supplies the main content of the Overlay component',
		},

		footer: {
			required: false,
			scoped: false,
			description: 'Supplies the footer content of the Overlay component',
		},
	},

	data() {
		return {
			isOpen: false,
			isOversized: false,
			uploadCloseable: true,
			showTrap: false,
			lastFocusedElement: null,
		};
	},

	computed: {
		hasContentSlot() {
			return !!this.$slots.content || !!this.$scopedSlots.content;
		},

		hasFooterSlot() {
			return !!this.$slots.footer || !!this.$scopedSlots.footer;
		},
	},

	mounted() {
		if (!this.hasContentSlot) {
			this.$emit('close');
			return;
		}

		this.$on('pageChange', (currentPage) => {
			const nonClosablePages = ['image-crop', 'preview'];
			const result = !nonClosablePages.includes(currentPage);
			this.uploadCloseable = result;
		});

		this.lastFocusedElement = document.activeElement;
		this.showTrap = true;
		this.isOpen = true;

		setTimeout(() => {
			document.body.classList.add('is-overlay-opened');
		}, 100);

		window.addEventListener('resize', this.onResize);
		window.addEventListener('keyup', this.checkForEscape);

		this.$nextTick(() => {
			this.onResize();
		});

		if (this.$refs.container) {
			setTimeout(() => {
				this.$refs.container.scrollTop = 0;
			}, 50);
		}
	},

	beforeDestroy() {
		document.body.classList.remove('is-overlay-opened');

		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('keyup', this.checkForEscape);
	},

	methods: {
		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27) {
				this.$emit('close');
			}
		},

		close(emit = true) {
			this.showTrap = false;

			setTimeout(() => {
				document.body.classList.remove('is-overlay-opened');
				this.lastFocusedElement.focus();
			}, 100);

			if (emit) {
				this.$emit('close');
			}
		},

		onResize() {
			let contentHeight = 0;
			let containerHeight = 0;

			if (this.$refs.heading) {
				contentHeight += this.$refs.heading.clientHeight;
			}

			if (this.$refs.content) {
				contentHeight += this.$refs.content.clientHeight;
			}

			if (this.$refs.container) {
				containerHeight = this.$refs.container.clientHeight;
			}

			this.isOversized = (contentHeight > containerHeight - 100);
		},
	},
};
</script>

<style lang="scss" scoped src="./overlay.scss" />
